import { useMutationWithContext } from '../../../../common/hooks/useMutationWithContext';
import { failedToastMessage, successToastMessage } from '../../../../common/utils/commonUtils';
import { orderClient } from '../../../../supply/opportunity/orders/order';
import { confirmResourceDeal, confirmResourceOrder, confirmResourceQuote } from '../utils/resourceOrdersApi';
import { CONFIRM_ORDER_RESPONSE_KEY, RESOURCE_ORDERS_TYPE_ID } from '../utils/resourceOrdersConstants';
import { ResourceOrderInputType } from './saveResourceOrder';

export const useConfirmResourceOrder = (orderTypeId: keyof typeof RESOURCE_ORDERS_TYPE_ID) => {
    const [confirmOrder, { loading: confirmingOrder }] = useMutationWithContext(confirmResourceOrder, orderClient);
    const [confirmQuote, { loading: confirmingQuote }] = useMutationWithContext(confirmResourceQuote, orderClient);
    const [confirmDeal, { loading: confirmingDeal }] = useMutationWithContext(confirmResourceDeal, orderClient);

    const mutations = {
        [RESOURCE_ORDERS_TYPE_ID.order]: confirmOrder,
        [RESOURCE_ORDERS_TYPE_ID.quote]: confirmQuote,
        [RESOURCE_ORDERS_TYPE_ID.deal]: confirmDeal
    };
    const confirmMutation = mutations[orderTypeId];

    const confirmOrderForResource = ({ resourceId, orderInput, workflowInput, noteInput }: ResourceOrderInputType) => {
        return new Promise((resolve, reject) => {
            return confirmMutation({
                variables: {
                    resourceId,
                    orderInput,
                    workflowInput,
                    noteInput
                }
            })
                .then((confirmProductionOrder) => {
                    const values = { name: orderInput?.name, type: orderTypeId };
                    const responseNameKey =
                        orderTypeId === RESOURCE_ORDERS_TYPE_ID.order
                            ? CONFIRM_ORDER_RESPONSE_KEY.order
                            : CONFIRM_ORDER_RESPONSE_KEY.quote;
                    const response = confirmProductionOrder.data[responseNameKey];

                    if (response?.errors) {
                        failedToastMessage('resource.order.failed.message', values);
                        reject(response.errors);
                    } else {
                        successToastMessage('resource.order.success.message', values);
                        resolve(response.reference);
                    }
                })
                .catch((err) => {
                    failedToastMessage('resource.order.failed.message', { name: orderInput?.name, type: orderTypeId });
                    reject(err);
                });
        });
    };

    const confirmDealForResource = ({ orderInput, noteInput }: ResourceOrderInputType) => {
        return new Promise((resolve, reject) => {
            return confirmMutation({
                variables: {
                    orderInput,
                    noteInput
                }
            })
                .then((confirmProductionDeal) => {
                    const values = { name: orderInput?.name, type: orderTypeId };
                    const response = confirmProductionDeal.data['confirmProductionDeal'];

                    if (response?.errors) {
                        failedToastMessage('resource.order.failed.message', values);
                        reject(response.errors);
                    } else {
                        successToastMessage('resource.order.success.message', values);
                        resolve(response.reference);
                    }
                })
                .catch((err) => {
                    failedToastMessage('resource.order.failed.message', { name: orderInput?.name, type: orderTypeId });
                    reject(err);
                });
        });
    };

    return { confirmOrderForResource, confirmingOrder, confirmingQuote, confirmDealForResource, confirmingDeal };
};
